
import { useToolkitPractices } from "@/hooks/useToolkitPractices";
import { PracticeBuilder } from "@/components/explore/PracticeBuilder";
import sensoryGroundingVideo from "@/assets/grounding-videos/sensory-grounding.mp4.asset.json";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Sparkles,
  ArrowRight,
  Search,
  X,
  ShieldCheck,
  BookOpen,
  Waves,
  HeartHandshake,
  Heart,
  Compass,
  Sprout,
  Brain,
  NotebookPen,
  Users,
  Check,
} from "lucide-react";
import { CalmingSounds } from "@/components/explore/ExperienceRecoveryTrauma";

import { ExploreMeditationPlayer } from "@/components/explore/ExploreMeditationPlayer";
import { ExploreDreamJournal } from "@/components/explore/ExploreDreamJournal";
import { ExploreShadowJournal } from "@/components/explore/ExploreShadowJournal";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselSwipeHint,
} from "@/components/ui/carousel";

import { Shelf } from "@/components/mobile/Shelf";
import { PlatformScaleStats } from "@/components/PlatformScaleStats";
import { useMembershipCta } from "@/hooks/useMembershipCta";
import { startMembershipJourney, MEMBERSHIP_JOURNEY_PATH } from "@/lib/funnel";

import {
  trackExplore,
  noteExploreExperience,
  markExploreCta,
} from "@/lib/explore-analytics";

export const Route = createFileRoute("/explore")({
  head: () => ({
    meta: [
      { title: "Explore Recovery Trauma™ — Free Tools, Meditation & Journals" },
      {
        name: "description",
        content:
          "See inside Recovery Trauma™: seven complete free resources, a full guided meditation, dream and shadow journals and calming sounds — no account needed.",
      },
      { property: "og:title", content: "Explore Recovery Trauma™ — See What's Inside" },
      {
        property: "og:description",
        content:
          "Try seven free resources, a full meditation, the journals and calming sounds before you join.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/explore" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/explore" }],
  }),
  component: ExplorePage,
});



const FREE_RESOURCES = [
  {
    kind: "Learning Hub guide",
    title: "Window of Tolerance",
    copy: "How your nervous system moves between calm, overwhelm and shutdown — and how to widen the space in between.",
    cta: "Read the guide",
    icon: Waves,
    to: "/learn/window-of-tolerance",
    params: {},
    iconClass: "bg-teal-100 text-teal-700 dark:bg-teal-950/60 dark:text-teal-300",
    tint: "border-teal-500/25 bg-gradient-to-br from-teal-500/10 via-card to-card",
  },
  {
    kind: "Learning Hub guide",
    title: "Understanding Your Triggers",
    copy: "What triggers are, why they feel so sudden, and gentle ways to work with them.",
    cta: "Read the guide",
    icon: BookOpen,
    to: "/learn/$topic",
    params: { topic: "triggers" },
    iconClass: "bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300",
    tint: "border-sky-500/25 bg-gradient-to-br from-sky-500/10 via-card to-card",
  },
  {
    kind: "Self-assessment",
    title: "Fight, Flight, Freeze & Fawn",
    copy: "A reflective look at which survival responses you lean on. Full results and resources included.",
    cta: "Take the assessment",
    icon: ShieldCheck,
    to: "/learn/assessments/$slug",
    params: { slug: "trauma-responses" },
    iconClass: "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300",
    tint: "border-amber-500/25 bg-gradient-to-br from-amber-500/10 via-card to-card",
  },
  {
    kind: "Self-assessment",
    title: "Attachment Patterns",
    copy: "How you tend to move toward closeness, distance and repair. Non-diagnostic, yours to keep.",
    cta: "Take the assessment",
    icon: HeartHandshake,
    to: "/learn/assessments/$slug",
    params: { slug: "attachment" },
    iconClass: "bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300",
    tint: "border-rose-500/25 bg-gradient-to-br from-rose-500/10 via-card to-card",
  },
  {
    kind: "Gentle exercises",
    title: "Self-Love",
    copy: "Gentle exercises to explore how you speak to yourself, what you need, and how you can begin building a kinder relationship with yourself.",
    cta: "Explore Self-Love",
    icon: Heart,
    to: "/self-love",
    params: {},
    iconClass: "bg-pink-100 text-pink-700 dark:bg-pink-950/60 dark:text-pink-300",
    tint: "border-pink-500/25 bg-gradient-to-br from-pink-500/10 via-card to-card",
  },
  {
    kind: "Reflective exercise",
    title: "Building a Life Worth Living",
    copy: "Explore what matters to you, what you want more of in your life and the small steps that can begin moving you toward it.",
    cta: "Try this exercise",
    icon: Sprout,
    to: "/integrate/life-worth-living",
    params: {},
    iconClass: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300",
    tint: "border-emerald-500/25 bg-gradient-to-br from-emerald-500/10 via-card to-card",
  },
  {
    kind: "Reflective exercise",
    title: "Self-Leadership",
    copy: "Explore how you can respond to yourself with more curiosity, steadiness and compassion rather than automatically reacting from fear or old survival patterns.",
    cta: "Explore Self-Leadership",
    icon: Compass,
    to: "/integrate/self-leadership",
    params: {},
    iconClass: "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300",
    tint: "border-indigo-500/25 bg-gradient-to-br from-indigo-500/10 via-card to-card",
  },
] as const;

const MEMBER_PILLARS = [
  {
    title: "Understand",
    copy: "Make sense of your experience with trauma education, attachment theory and nervous-system science — all explained in plain language.",
    icon: Brain,
    iconClass: "bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300",
    tint: "border-sky-500/25 bg-gradient-to-b from-sky-500/10 via-card to-background",
    cta: "Browse the Learning Hub",
    ctaTo: "/learn",
    includes: [
      { label: "Window of Tolerance", free: true, to: "/learn/window-of-tolerance" },
      { label: "Fight, Flight, Freeze & Fawn", free: true, to: "/learn/assessments/trauma-responses" },
      { label: "Attachment patterns", free: true, to: "/learn/assessments/attachment" },
      { label: "27 self-assessments", to: "/learn/assessments" },
      { label: "Nervous-system education" },
      { label: "Relationships & trauma" },
    ],
  },
  {
    title: "Regulate",
    copy: "Calm your body in real time with grounding, breathing, somatic practices, meditation, yoga and calming sounds.",
    icon: Waves,
    iconClass: "bg-teal-100 text-teal-700 dark:bg-teal-950/60 dark:text-teal-300",
    tint: "border-teal-500/25 bg-gradient-to-b from-teal-500/10 via-card to-background",
    cta: "Explore regulation tools",
    ctaTo: "/grounding",
    includes: [
      { label: "Grounding exercises", to: "/grounding" },
      { label: "Breathing practices", to: "/breathing" },
      { label: "Guided meditations", to: "/meditations" },
      { label: "Somatic & yoga practices" },
      { label: "Calming sounds & white noise", to: "/sounds" },
      { label: "Sleep & rest support", to: "/sleep" },
    ],
  },
  {
    title: "Process",
    copy: "Put what's inside into words, art and movement with private journals, shadow work, inner-child exercises and EFT.",
    icon: NotebookPen,
    iconClass: "bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300",
    tint: "border-violet-500/25 bg-gradient-to-b from-violet-500/10 via-card to-background",
    cta: "Open the journals",
    ctaTo: "/journals",
    includes: [
      { label: "Dream journal", to: "/dreams" },
      { label: "Shadow work journal", to: "/shadow-work" },
      { label: "Inner child work" },
      { label: "Music journal", to: "/music-journal" },
      { label: "EFT & reflective exercises" },
      { label: "Private, saved only to you" },
    ],
  },
  {
    title: "Integrate",
    copy: "Build a life beyond survival mode with self-love, boundaries, identity, values and the future you want to grow into.",
    icon: Sprout,
    iconClass: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300",
    tint: "border-emerald-500/25 bg-gradient-to-b from-emerald-500/10 via-card to-background",
    cta: "Start integrating",
    ctaTo: "/integrate/life-worth-living",
    includes: [
      { label: "Self-Love", free: true, to: "/self-love" },
      { label: "Building a Life Worth Living", free: true, to: "/integrate/life-worth-living" },
      { label: "Self-Leadership", free: true, to: "/integrate/self-leadership" },
      { label: "Boundaries & identity" },
      { label: "Values & relationships" },
      { label: "IFS & parts work" },
    ],
  },
] as const;

/**
 * The Understand library, grouped by theme. Every entry links to a page that
 * already exists; `members` marks the ones inside the membership.
 */
const UNDERSTAND_TRACKS = [
  {
    title: "Trauma & the nervous system",
    copy: "What trauma does to a body, why survival responses switch on, and how states shift through the day.",
    icon: Brain,
    iconClass: "bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300",
    tint: "border-sky-500/25 bg-gradient-to-b from-sky-500/10 via-card to-background",
    links: [
      { label: "Window of Tolerance", to: "/learn/window-of-tolerance" },
      { label: "Fight, flight, freeze & fawn", to: "/learn/assessments/$slug", params: { slug: "trauma-responses" } },
      { label: "The polyvagal ladder", to: "/learn/polyvagal-ladder" },
      { label: "Shame-based trauma", to: "/learn/shame-based-trauma" },
    ],
  },
  {
    title: "Attachment",
    copy: "How early bonds shaped what safety, closeness and distance feel like now — without labelling anyone.",
    icon: HeartHandshake,
    iconClass: "bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300",
    tint: "border-rose-500/25 bg-gradient-to-b from-rose-500/10 via-card to-background",
    links: [
      { label: "Attachment patterns", to: "/learn/assessments/$slug", params: { slug: "attachment" } },
      { label: "Bowlby, Ainsworth & Winnicott", to: "/learn/assessments/pioneers", members: true },
      { label: "The trauma healing journey", to: "/learn/assessments/trauma-healing-journey", members: true },
    ],
  },
  {
    title: "Relationship patterns",
    copy: "Why certain dynamics feel so powerful, what those patterns may be protecting, and what gentler connection can look like.",
    icon: Users,
    iconClass: "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300",
    tint: "border-amber-500/25 bg-gradient-to-b from-amber-500/10 via-card to-background",
    links: [
      { label: "Trauma, overlap & relationships", to: "/learn/personality", members: true },
      { label: "Parts work & Internal Family Systems", to: "/learn/what-is-ifs" },
      { label: "Skills for intense emotions (DBT)", to: "/learn/dbt" },
    ],
  },
  {
    title: "Personality & personality disorders",
    copy: "Educational profiles and guides that separate traits from diagnosis. Recovery Trauma™ never diagnoses anyone.",
    icon: Compass,
    iconClass: "bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300",
    tint: "border-violet-500/25 bg-gradient-to-b from-violet-500/10 via-card to-background",
    links: [
      { label: "Understanding personality", to: "/learn/personality", members: true },
      { label: "Pioneers & thinkers", to: "/learn/assessments/pioneers", members: true },
      { label: "Plain-language glossary", to: "/learn/glossary" },
    ],
  },
] as const;


function ExplorePage() {
  const navigate = useNavigate();
  const viewed = useRef(false);

  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    trackExplore("explore_page_viewed", { path: "/explore" });
  }, []);

  const { showJoinCta, isActiveMember } = useMembershipCta();

  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  type SearchResult = {
    label: string;
    group: string;
    to: string;
    params?: Record<string, string>;
    members?: boolean;
    free?: boolean;
  };

  const { data: dbPractices } = useToolkitPractices();

  const results = useMemo<SearchResult[]>(() => {
    if (!q) return [];
    const out: SearchResult[] = [];
    for (const p of dbPractices ?? []) {
      if (`${p.title} ${p.blurb} ${(p.tips ?? []).join(" ")}`.toLowerCase().includes(q)) {
        out.push(
          p.kind === "somatic"
            ? { label: p.title, group: "Somatic Exercises", to: "/somatic/$practice", params: { practice: p.id }, members: true }
            : { label: p.title, group: "Yoga", to: "/yoga", members: true },
        );
      }
    }
    for (const r of FREE_RESOURCES) {
      if (`${r.title} ${r.copy} ${r.kind}`.toLowerCase().includes(q)) {
        out.push({ label: r.title, group: "Free tools", to: r.to, params: r.params, free: true });
      }
    }
    for (const t of UNDERSTAND_TRACKS) {
      for (const l of t.links) {
        if (`${l.label} ${t.title}`.toLowerCase().includes(q)) {
          out.push({
            label: l.label,
            group: `Understand · ${t.title}`,
            to: l.to,
            params: "params" in l ? l.params : undefined,
            members: "members" in l ? l.members : undefined,
          });
        }
      }
    }
    for (const p of MEMBER_PILLARS) {
      for (const i of p.includes) {
        if ("to" in i && `${i.label} ${p.title}`.toLowerCase().includes(q)) {
          out.push({
            label: i.label,
            group: `Membership · ${p.title}`,
            to: i.to,
            free: "free" in i ? i.free : undefined,
          });
        }
      }
    }
    return out;
  }, [q, dbPractices]);

  const goToCheckout = (cta: string) => {
    // Members already have access: send them to the real member space
    // instead of the purchase funnel.
    if (isActiveMember) {
      const memberDest: Record<string, string> = {
        dream_journal: "/dreams",
        shadow_journal: "/shadow-journey",
      };
      void navigate({ to: (memberDest[cta] ?? "/dashboard") as "/dashboard" });
      return;
    }
    markExploreCta(cta);
    startMembershipJourney(cta, "explore");
    trackExplore("explore_membership_clicked", { cta });
    void navigate({ to: MEMBERSHIP_JOURNEY_PATH });
  };

  const inlineJoin = (cta: string, text: string) =>
    showJoinCta ? <InlineJoin text={text} onJoin={() => goToCheckout(cta)} /> : null;

  return (
    <div className="pb-16">
      {/* ── Hero ─────────────────────────────────────────────── */}
      <header className="mx-auto max-w-4xl px-4 pt-8 sm:pt-12">
        <div className="text-center">
          <span className="inline-flex min-h-9 items-center gap-1.5 rounded-full border border-gold/40 bg-background/80 px-3.5 py-1 text-xs font-medium text-foreground">
            <Sparkles className="h-3 w-3 text-[var(--gold-deep)]" aria-hidden />
            {isActiveMember ? "You're a member · Everything is unlocked" : "Explore Recovery Trauma™ · Free to look around"}
          </span>

          <h1 className="mt-4 font-display text-3xl leading-tight text-foreground sm:text-5xl">
            Explore Recovery Trauma™
          </h1>
          <p className="mt-2 font-display text-lg tracking-tight text-[var(--gold-deep)] sm:text-2xl">
            Understand · Regulate · Process · Integrate
          </p>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            A gentle, all-in-one place for trauma recovery — with practical tools, guided practices
            and real support, so you can move forward at your own pace.
          </p>

          <div className="relative mx-auto mt-5 max-w-md">
            <Search className="pointer-events-none absolute start-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tools, guides, journals…"
              aria-label="Search Recovery Trauma™ tools and guides"
              className="w-full rounded-full border border-border/60 bg-card py-2.5 ps-10 pe-9 text-sm text-foreground placeholder:text-muted-foreground outline-none transition focus:border-[var(--gold-deep)] focus:ring-2 focus:ring-[var(--gold-deep)]/20"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute end-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="mt-6 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            {showJoinCta && (
              <button
                type="button"
                onClick={() => goToCheckout("hero")}
                className="btn-gold min-h-11 justify-center px-8 text-base"
              >
                Join Recovery Trauma™
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
            <a
              href="#free-experiences"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-gold/40 bg-background px-6 text-sm font-medium text-foreground transition hover:bg-muted/60"
            >
              Explore Free Tools
            </a>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-4">
        {q ? (
          <div className="pt-8">
            <p className="text-sm text-muted-foreground">
              {results.length === 0
                ? `Nothing matches “${query.trim()}” — try a different word.`
                : `${results.length} result${results.length === 1 ? "" : "s"} for “${query.trim()}”`}
            </p>
            <ul className="mt-4 space-y-2.5">
              {results.map((r) => (
                <li key={`${r.group}-${r.label}`}>
                  <Link
                    to={r.to as never}
                    params={r.params as never}
                    className="flex items-center gap-3 rounded-2xl border border-border/60 bg-card px-4 py-3 transition hover:-translate-y-0.5 hover:border-gold/40 hover:shadow-sm"
                  >
                    <ArrowRight className="h-4 w-4 shrink-0 text-[var(--gold-deep)]" aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground">
                        {r.label}
                      </span>
                      <span className="block truncate text-[11px] text-muted-foreground">
                        {r.group}
                      </span>
                    </span>
                    {r.free ? <FreeBadge /> : null}
                    {r.members ? <MembersBadge /> : null}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : (
        <div className="space-y-12 pt-10 sm:space-y-14">
          {/* ── The Four Areas ──────────────────────────────── */}
          <FourAreasGrid />

          {/* ── Try a few free experiences ─────────────────── */}
          <section id="free-experiences" aria-labelledby="free-exp-heading" className="scroll-mt-24">
            <div className="text-center">
              <h2 id="free-exp-heading" className="font-display text-2xl leading-tight text-foreground sm:text-3xl">
                Try a few free experiences
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Get a real feel for the platform. No account needed.
              </p>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-3">
              {FREE_EXPERIENCES.map((x) => (
                <a
                  key={x.href}
                  href={x.href}
                  className={`flex flex-col rounded-2xl border p-3.5 transition hover:-translate-y-0.5 hover:shadow-md ${x.tint}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-2xl" aria-hidden>{x.emoji}</span>
                    <FreeBadge />
                  </div>
                  <p className="mt-2 font-display text-[15px] leading-snug text-foreground">{x.title}</p>
                  <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">{x.copy}</p>
                </a>
              ))}
            </div>
          </section>

          {/* ── Regulate experiences ───────────────────────── */}
          <Section
            id="free-grounding-video"
            step="02"
            eyebrow="Regulate · Free practice"
            title="✋ Sensory Grounding"
            blurb="A short, follow-along practice using touch, sound and sight to notice what is here right now."
            badge="free"
          >
            <figure className="overflow-hidden rounded-2xl border border-gold/30 bg-card">
              <video
                src={sensoryGroundingVideo.url}
                controls
                playsInline
                preload="metadata"
                className="aspect-video w-full bg-black"
              >
                Your browser cannot play this video.
              </video>
              <figcaption className="px-4 py-3 text-xs leading-relaxed text-muted-foreground">
                Go at your own pace, and stop whenever you would like to. This is a wellbeing
                practice, not clinical treatment.
              </figcaption>
            </figure>
          </Section>

          <Section
            id="meditation"
            step="02"
            eyebrow="Regulate · Guided meditation"
            title="Reconnecting With Your Inner Child"
            blurb="The complete track, exactly as members hear it."
            badge="free"
          >
            <ExploreMeditationPlayer />
          </Section>

          {inlineJoin("explore_after_meditation", "Members get dozens of meditations, breathing and somatic practices.")}

          {/* ── Process experiences ────────────────────────── */}
          <Section
            id="journals"
            step="03"
            eyebrow="Process · Journals"
            title="Try one of our journals"
            blurb="Try the Dream Journal below — members keep their entries saved in their private journal, on every device."
            badge="free"
          >
            <div className="space-y-5">
              <ExploreDreamJournal />
              <div className="rounded-3xl border border-[var(--qa-rose-ink)]/20 bg-gradient-to-br from-[var(--qa-rose-tint)] via-card to-[var(--qa-gold-tint)] p-4 sm:p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--gold-deep)]">
                  Shadow Journal · Reflection exercise
                </p>
                <div className="mt-3">
                  <ExploreShadowJournal onJoin={() => goToCheckout("shadow_journal")} />
                </div>
              </div>
            </div>
          </Section>

          {/* ── What could change for you? ─────────────────── */}
          <WhatCouldChange showJoin={showJoinCta} onJoin={() => goToCheckout("explore_what_could_change")} />

          {/* ── More free tools ─────────────────────────────── */}
          <Section
            id="free-resources"
            step="✦"
            eyebrow="More free tools"
            title="Free guides & exercises you can use right now"
            blurb="Seven complete resources — no account, and no membership wall part-way through."
          >
            <Carousel opts={{ align: "start" }} className="w-full">
              <CarouselContent className="-ms-3">
                {FREE_RESOURCES.map((item) => {
                  const Icon = item.icon;
                  return (
                    <CarouselItem
                      key={item.title}
                      className="basis-[82%] ps-3 sm:basis-1/2 lg:basis-1/3"
                    >
                      <Link
                        to={item.to as never}
                        params={item.params as never}
                        onClick={() =>
                          trackExplore("explore_free_resource_clicked", { resource: item.title })
                        }
                        className={`flex h-full flex-col rounded-2xl border p-4 transition hover:-translate-y-0.5 hover:shadow-md ${item.tint}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span
                            className={`grid h-10 w-10 shrink-0 place-items-center rounded-2xl ${item.iconClass}`}
                          >
                            <Icon className="h-5 w-5" aria-hidden />
                          </span>
                          <FreeBadge />
                        </div>
                        <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                          {item.kind}
                        </p>
                        <p className="mt-0.5 font-display text-base leading-snug text-foreground">
                          {item.title}
                        </p>
                        <p className="mt-1 text-sm leading-relaxed text-foreground/70">
                          {item.copy}
                        </p>
                        <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[var(--gold-deep)]">
                          {item.cta} <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                        </span>
                      </Link>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <CarouselPrevious className="hidden sm:flex" />
              <CarouselNext className="hidden sm:flex" />
              <CarouselSwipeHint />
            </Carousel>
          </Section>

          <Section
            id="practice-builder"
            step="02"
            eyebrow="Regulate · Build your own"
            title="Practice builder"
            blurb="Choose what you're working with, how gentle and how long — we'll put together a sequence of somatic exercises and yoga."
          >
            <PracticeBuilder />
          </Section>

          <Section
            id="regulate"
            step="02"
            eyebrow="Regulate · Calming sounds"
            title="Come back to your body"
            blurb="A sound bath, white noise and pink noise you can use right now."
            badge="free"
          >
            <CalmingSounds
              onPlay={(kind) => {
                noteExploreExperience("calming_sounds");
                trackExplore("explore_sound_started", { sound: kind });
              }}
            />
          </Section>

          {inlineJoin("explore_after_sounds", "Sleep support, white noise and full sound library inside membership.")}

          <Section
            id="understand"
            step="01"
            eyebrow="Understand · Learning library"
            title="Make sense of what happened to you"
            blurb="Trauma and the nervous system, attachment, relationship patterns and personality — explained in plain language. Education and self-reflection only; nothing here diagnoses anyone."
          >
            <Shelf cols={2} cardWidth="lg" showDots>
              {UNDERSTAND_TRACKS.map((track) => {
                const Icon = track.icon;
                return (
                  <div
                    key={track.title}
                    className={`flex h-full flex-col rounded-3xl border p-5 shadow-sm ${track.tint}`}
                  >
                    <span
                      className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl ${track.iconClass}`}
                    >
                      <Icon className="h-5 w-5" aria-hidden />
                    </span>
                    <h3 className="mt-3 font-display text-lg leading-snug text-foreground">
                      {track.title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                      {track.copy}
                    </p>
                    <ul className="mt-3 space-y-0">
                      {track.links.map((item) => (
                        <li key={item.label}>
                          <Link
                            to={item.to as never}
                            params={("params" in item ? item.params : {}) as never}
                            onClick={() =>
                              trackExplore("explore_understand_clicked", { topic: item.label })
                            }
                            className="inline-flex min-h-[44px] items-center gap-1.5 text-sm font-medium text-foreground underline-offset-4 hover:underline"
                          >
                            <ArrowRight className="h-3.5 w-3.5 text-[var(--gold-deep)]" aria-hidden />
                            {item.label}
                            {"members" in item && item.members ? (
                              <span className="rounded-full border border-gold/40 bg-gold/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--gold-deep)]">
                                Members
                              </span>
                            ) : null}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </Shelf>
            <Link
              to="/learn"
              className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[var(--gold-deep)] underline underline-offset-4"
            >
              Browse the whole Learning Hub <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </Section>

          <figure className="mx-auto max-w-xl rounded-2xl border border-gold/30 bg-card/85 p-4 text-start shadow-sm sm:p-5">
            <figcaption className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
              A note from Jana, founder
            </figcaption>
            <blockquote className="mt-2 text-sm leading-relaxed text-muted-foreground">
              “I built Recovery Trauma™ because I live with C-PTSD. After losing my mum as a child,
              I spent years in survival mode, piecing together what actually helped me — grounding,
              journaling, sound, inner child work. This is the gentle, all-in-one place I wish I'd
              had. Everything here comes from lived experience, not a textbook.”
            </blockquote>
          </figure>

          <PlatformScaleStats />

          {/* ── Closing ─────────────────────────────────────── */}
          <section className="rounded-3xl border border-gold/30 bg-gradient-to-br from-[var(--gold-soft)]/40 via-card to-card p-6 text-center sm:p-8">
            <h2 className="font-display text-2xl leading-tight text-foreground sm:text-3xl">
              You don't have to know where to start.
            </h2>
            <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">
              Start with what feels most useful today. Learn something, regulate, reflect, create —
              and come back whenever you need.
            </p>
            <div className="mt-5 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
              {showJoinCta && (
                <button
                  type="button"
                  onClick={() => goToCheckout("explore_footer")}
                  className="btn-gold min-h-11 justify-center px-8 text-base"
                >
                  Join Recovery Trauma™
                  <ArrowRight className="h-4 w-4" />
                </button>
              )}
              <a
                href="#free-experiences"
                className="inline-flex min-h-11 items-center justify-center rounded-full border border-gold/40 bg-background px-6 text-sm font-medium text-foreground transition hover:bg-muted/60"
              >
                Explore Free Tools
              </a>
            </div>
          </section>
        </div>
        )}
      </div>
    </div>
  );
}

const FREE_EXPERIENCES = [
  { emoji: "✋", title: "Sensory Grounding", copy: "A short follow-along practice using touch, sound and sight.", href: "#free-grounding-video", tint: "border-teal-500/25 bg-gradient-to-br from-teal-500/10 via-card to-card" },
  { emoji: "🎧", title: "Guided Meditation", copy: "Reconnecting With Your Inner Child.", href: "#meditation", tint: "border-amber-500/25 bg-gradient-to-br from-amber-500/10 via-card to-card" },
  { emoji: "🌙", title: "Dream Journal", copy: "Write down what stayed with you from your dream.", href: "#journals", tint: "border-rose-500/25 bg-gradient-to-br from-rose-500/10 via-card to-card" },
] as const;

const FOUR_AREAS = [
  {
    num: "01", title: "Understand", tagline: "Make sense of what happened to you.", icon: Brain, to: "/learn",
    items: ["Trauma & nervous system", "Attachment patterns", "Trauma responses", "Relationships", "Self-assessments", "Educational guides"],
    iconClass: "bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300",
    tint: "border-sky-500/30 bg-gradient-to-b from-sky-500/12 via-card to-card",
  },
  {
    num: "02", title: "Regulate", tagline: "Come back to your body.", icon: Waves, to: "/grounding",
    items: ["Grounding practices", "Breathing & somatic tools", "Yoga & gentle movement", "Calming sounds", "Guided meditations", "Practice Builder"],
    iconClass: "bg-teal-100 text-teal-700 dark:bg-teal-950/60 dark:text-teal-300",
    tint: "border-teal-500/30 bg-gradient-to-b from-teal-500/12 via-card to-card",
  },
  {
    num: "03", title: "Process", tagline: "Give what you've been carrying somewhere to go.", icon: NotebookPen, to: "/journals",
    items: ["Journaling tools", "Inner child work", "Shadow work", "Trigger reflection", "Trauma-bond work", "Deeper reflective exercises"],
    iconClass: "bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300",
    tint: "border-rose-500/30 bg-gradient-to-b from-rose-500/12 via-card to-card",
  },
  {
    num: "04", title: "Integrate", tagline: "Bring what you're learning into your life.", icon: Sprout, to: "/integrate/life-worth-living",
    items: ["Creative exercises", "Ongoing journeys", "Self-reflection", "Relationship tools", "Everyday practices", "And more…"],
    iconClass: "bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300",
    tint: "border-violet-500/30 bg-gradient-to-b from-violet-500/12 via-card to-card",
  },
] as const;

function FourAreasGrid() {
  return (
    <section aria-labelledby="four-areas-heading">
      <div className="text-center">
        <h2 id="four-areas-heading" className="font-display text-2xl leading-tight text-foreground sm:text-3xl">
          The Four Areas of Recovery Trauma™
        </h2>
        <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
          Everything on the platform is organised around four interconnected areas. Explore them in any order.
        </p>
      </div>
      <Carousel opts={{ align: "start" }} className="mt-5 w-full">
        <CarouselContent className="-ms-3">
          {FOUR_AREAS.map((a) => {
            const Icon = a.icon;
            return (
              <CarouselItem key={a.title} className="basis-[82%] ps-3 sm:basis-1/2 lg:basis-1/4">
                <article className={`flex h-full flex-col rounded-3xl border p-5 shadow-sm ${a.tint}`}>
                  <span className={`grid h-11 w-11 place-items-center rounded-2xl ${a.iconClass}`}>
                    <Icon className="h-5 w-5" aria-hidden />
                  </span>
                  <p className="mt-3 font-display text-sm text-[var(--gold-deep)]">{a.num}</p>
                  <h3 className="font-display text-xl uppercase tracking-tight text-foreground">{a.title}</h3>
                  <p className="mt-1 text-sm leading-snug text-muted-foreground">{a.tagline}</p>
                  <ul className="mt-3 flex-1 space-y-1.5">
                    {a.items.map((i) => (
                      <li key={i} className="flex items-center gap-2 text-[13.5px] text-foreground/85">
                        <Check className="h-3.5 w-3.5 shrink-0 text-[var(--gold-deep)]" aria-hidden />
                        {i}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={a.to as never}
                    className="mt-4 inline-flex min-h-11 items-center justify-center gap-1.5 rounded-full border border-gold/40 bg-background/80 px-4 text-sm font-medium text-foreground transition hover:bg-muted/60"
                  >
                    Explore {a.title} <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                  </Link>
                </article>
              </CarouselItem>
            );
          })}
        </CarouselContent>
        <CarouselPrevious className="hidden sm:flex lg:hidden" />
        <CarouselNext className="hidden sm:flex lg:hidden" />
        <CarouselSwipeHint />
      </Carousel>
    </section>
  );
}

const CHANGES = [
  { icon: Brain, title: "Understand yourself more deeply", copy: "Recognise your patterns, triggers and emotions — and begin to understand why they happen." },
  { icon: Sprout, title: "Feel safer in your own body", copy: "Build practical ways to work with overwhelm, anxiety, stress and shutdown." },
  { icon: Heart, title: "Respond differently to old patterns", copy: "Create more space between what triggers you and how you respond." },
  { icon: Users, title: "Build healthier relationships", copy: "Explore attachment, boundaries, people-pleasing, trauma bonds and relationship patterns." },
  { icon: Compass, title: "Reconnect with yourself", copy: "Understand what you feel, what you need and who you are beyond survival mode." },
  { icon: HeartHandshake, title: "Have support you can return to", copy: "Different tools for different moments, all available in one place." },
] as const;

function WhatCouldChange({ showJoin, onJoin }: { showJoin: boolean; onJoin: () => void }) {
  return (
    <section aria-labelledby="change-heading" className="rounded-3xl border border-gold/30 bg-gradient-to-br from-[var(--gold-soft)]/35 via-card to-card p-5 sm:p-8">
      <h2 id="change-heading" className="font-display text-2xl leading-tight text-foreground sm:text-3xl">
        What could change for you?
      </h2>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
        Recovery Trauma™ is here to help you move from just surviving to living a life that feels more like yours.
      </p>
      <ul className="mt-5 grid gap-4 sm:grid-cols-2">
        {CHANGES.map(({ icon: Icon, title, copy }) => (
          <li key={title} className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gold/15 text-[var(--gold-deep)]">
              <Icon className="h-5 w-5" aria-hidden />
            </span>
            <span className="min-w-0">
              <span className="block text-[15px] font-medium text-foreground">{title}</span>
              <span className="block text-[13px] leading-relaxed text-muted-foreground">{copy}</span>
            </span>
          </li>
        ))}
      </ul>
      <blockquote className="mx-auto mt-6 max-w-2xl border-s-2 border-gold/60 ps-4 font-display text-lg italic leading-snug text-foreground sm:text-xl">
        The goal isn't to become a different person. It's to understand yourself, support yourself
        and gradually build a life that isn't organised around survival.
      </blockquote>
      {showJoin && (
        <div className="mt-6 text-center">
          <button type="button" onClick={onJoin} className="btn-gold min-h-12 w-full justify-center px-8 text-base sm:w-auto">
            Join Recovery Trauma™
            <ArrowRight className="h-4 w-4" />
          </button>
          <p className="mt-2 text-xs text-muted-foreground">Monthly · 6-month · Yearly · Cancel anytime</p>
        </div>
      )}
    </section>
  );
}

function InlineJoin({ text, onJoin }: { text: string; onJoin: () => void }) {
  return (
    <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-sm text-muted-foreground">
      {text}
      <button
        type="button"
        onClick={onJoin}
        className="inline-flex min-h-10 items-center gap-1 font-medium text-[var(--gold-deep)] underline-offset-4 hover:underline"
      >
        Join Recovery Trauma™ <ArrowRight className="h-3.5 w-3.5" aria-hidden />
      </button>
    </p>
  );
}

function FreeBadge() {
  return (
    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
      Free
    </span>
  );
}

function MembersBadge() {
  return (
    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
      🔒 Members
    </span>
  );
}

function MembershipCta({
  heading,
  copy,
  onJoin,
  ctaLabel = "Join Recovery Trauma™",
}: {
  heading: string;
  copy: string;
  onJoin: () => void;
  ctaLabel?: string;
}) {
  return (
    <section className="rounded-3xl border border-gold/30 bg-card p-5 text-center sm:p-7">
      <h2 className="font-display text-2xl leading-tight text-foreground sm:text-3xl">{heading}</h2>
      <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">{copy}</p>
      <button
        type="button"
        onClick={onJoin}
        className="btn-gold mt-5 min-h-11 w-full justify-center px-8 text-base sm:w-auto"
      >
        {ctaLabel}
        <ArrowRight className="h-4 w-4" />
      </button>
    </section>
  );
}

function Section({
  id,
  step,
  eyebrow,
  title,
  blurb,
  badge,
  children,
}: {
  id: string;
  step: string;
  eyebrow: string;
  title: string;
  blurb: string;
  badge?: "free" | "members";
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <div className="mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-display text-sm text-[var(--gold-deep)]">{step}</span>
          <span className="h-px w-6 bg-gold/40" aria-hidden />
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
            {eyebrow}
          </span>
          {badge === "free" ? <FreeBadge /> : null}
          {badge === "members" ? <MembersBadge /> : null}
        </div>
        <h2 className="mt-2 font-display text-2xl leading-tight text-foreground sm:text-3xl">
          {title}
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
          {blurb}
        </p>
      </div>
      {children}
    </section>
  );
}
